import React, { useState, useEffect } from 'react';
import { fmtDate } from '@/lib/dateFormat';
import { useAuthHeaders } from '@/lib/auth-store';
import { useGetMe } from '@workspace/api-client-react';
import { useAuthStore } from '@/lib/auth-store';
import { useLocation } from 'wouter';
import { Layout } from '@/components/layout';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Store, Search, Pencil, Trash2, X, Shield, ShieldCheck, ChevronDown, ToggleLeft, ToggleRight, AlertTriangle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

const ADMIN_EMAIL = 'marketing@bove.com.au';
const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface FeatureFlagDef {
  key: string;
  label: string;
  description: string;
  adminDefault: boolean;
  group: string;
}

// adminDefault must mirror the nav's flagDefault behaviour:
//   true  = shown when no explicit flag is set (matches no flagDefault in layout.tsx)
//   false = hidden until explicitly enabled (matches flagDefault: false in layout.tsx)
const FEATURE_FLAGS: FeatureFlagDef[] = [
  // ── Overview ──────────────────────────────────────────────────────────────
  { key: 'ai_copilot',           label: 'SoraFlo AI Agent',       description: 'Natural-language AI command interface for store automation',    adminDefault: true,  group: 'Overview' },
  // ── Automation ────────────────────────────────────────────────────────────
  { key: 'flows',                label: 'Automation Flows',        description: 'Create and run Shopify automation flows',                       adminDefault: true,  group: 'Automation' },
  { key: 'customer_automations', label: 'Customer Automations',    description: 'Automated customer tag and segment rules',                      adminDefault: true,  group: 'Automation' },
  { key: 'logs',                 label: 'Run Logs',                description: 'View flow execution history and errors',                        adminDefault: true,  group: 'Automation' },
  // ── Sourcing ──────────────────────────────────────────────────────────────
  { key: 'scraper',              label: 'Product Scraper',         description: 'Scrape product data from external sources',                     adminDefault: true,  group: 'Sourcing' },
  { key: 'import',               label: 'Product Importer',        description: 'Bulk import products via CSV',                                  adminDefault: true,  group: 'Sourcing' },
  { key: 'price_scrape',         label: 'Price Scraper',           description: 'Scrape live RRP prices from a supplier category and push to Shopify by SKU', adminDefault: false, group: 'Sourcing' },
  { key: 'offroad_imports',      label: 'Offroad Imports Sync',    description: 'Off Road Imports scheduled sync and manual import',             adminDefault: true,  group: 'Sourcing' },
  // ── Wheel Pros ────────────────────────────────────────────────────────────
  { key: 'wheel_pros',           label: 'Wheel Pros Catalog',      description: 'Browse and import from the Wheel Pros product catalog',         adminDefault: true,  group: 'Wheel Pros' },
  { key: 'wp_sync',              label: 'WP Auto-Sync',            description: 'Automated Wheel Pros catalog sync',                             adminDefault: true,  group: 'Wheel Pros' },
  { key: 'wp_import_log',        label: 'WP Import Log',           description: 'Wheel Pros import history and diagnostics',                     adminDefault: true,  group: 'Wheel Pros' },
  // ── Catalogue ─────────────────────────────────────────────────────────────
  { key: 'product_updates',      label: 'Bulk Product Updater',    description: 'Mass update product fields across the store',                   adminDefault: true,  group: 'Catalogue' },
  { key: 'collections',          label: 'Collections',             description: 'Bulk create and manage collections',                            adminDefault: true,  group: 'Catalogue' },
  { key: 'metafield_update',     label: 'Metafield Update',        description: 'Bulk update Shopify metafields',                                adminDefault: true,  group: 'Catalogue' },
  { key: 'tag_update',           label: 'Bulk Tag Update',         description: 'Bulk add/remove product and customer tags',                     adminDefault: true,  group: 'Catalogue' },
  { key: 'product_type_fixer',   label: 'Fix Product Types',       description: 'Bulk correct product type values across the catalogue',         adminDefault: true,  group: 'Catalogue' },
  { key: 'sku_creator',          label: 'Bulk SKU Creator',        description: 'Generate and assign SKUs to products in bulk',                  adminDefault: true,  group: 'Catalogue' },
  { key: 'shopify_file_upload',  label: 'Shopify File Upload',     description: 'Scan CSVs for images and upload them to Shopify Files',         adminDefault: true,  group: 'Catalogue' },
  // ── Fitment Data ──────────────────────────────────────────────────────────
  { key: 'fitment',              label: 'Vehicle Fitment',         description: 'Vehicle fitment selector and badge push-to-theme',              adminDefault: true,  group: 'Fitment Data' },
  // ── Finance ───────────────────────────────────────────────────────────────
  { key: 'reconciliation',       label: 'Reconciliation',          description: 'Financial reconciliation reports',                              adminDefault: true,  group: 'Finance' },
  // ── Shipping ──────────────────────────────────────────────────────────────
  { key: 'supplier_sync',        label: 'Live Supplier Rates',     description: 'Real-time carrier rates from drop-ship suppliers at checkout',  adminDefault: false, group: 'Shipping' },
  // ── Inventory ─────────────────────────────────────────────────────────────
  { key: 'inventory_sync',       label: 'Supplier Inventory Sync', description: 'Pull live stock levels from a supplier store and sync by SKU',  adminDefault: false, group: 'Inventory' },
  // ── Purchase Orders ───────────────────────────────────────────────────────
  { key: 'purchase_orders',      label: 'Purchase Orders',         description: 'Create and manage purchase orders with suppliers',              adminDefault: false, group: 'Purchase Orders' },
  // ── Notifications ─────────────────────────────────────────────────────────
  { key: 'notify_me',            label: 'Back-in-Stock Alerts',    description: 'Let customers subscribe for email alerts when a product is back in stock', adminDefault: false, group: 'Notifications' },
  // ── Settings ──────────────────────────────────────────────────────────────
  { key: 'messaging',            label: 'Messaging',               description: 'SMS and messaging integration settings',                        adminDefault: true,  group: 'Settings' },
  { key: 'wheel_pros_settings',  label: 'WheelPros Account',       description: 'Wheel Pros API credentials and settings',                       adminDefault: true,  group: 'Settings' },
];

interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: 'user' | 'admin';
  featureFlags: Record<string, boolean>;
  createdAt: string;
  storeCount: number;
  planName?: string | null;
  planPriceCents?: number | null;
  stripeCustomerId?: string | null;
}

interface AdminStats {
  totalUsers: number;
  totalStores: number;
}

function getFlagState(flags: Record<string, boolean>, key: string, adminDefault: boolean): boolean | null {
  if (key in flags) return flags[key];
  return null;
}

function getEffectiveState(flags: Record<string, boolean>, key: string, adminDefault: boolean): boolean {
  if (key in flags) return flags[key]!;
  return adminDefault;
}

export function AdminPage() {
  const [, setLocation] = useLocation();
  const token = useAuthStore(s => s.token);
  const authOpts = useAuthHeaders();
  const { toast } = useToast();

  const { data: me, isLoading: meLoading } = useGetMe({
    request: authOpts,
    query: { enabled: !!token, retry: false },
  });

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<AdminStats>({ totalUsers: 0, totalStores: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<'user' | 'admin'>('user');
  const [editFlags, setEditFlags] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editPlanName, setEditPlanName] = useState('');
  const [editPlanPrice, setEditPlanPrice] = useState('');

  const isAdmin = me && (me.email === ADMIN_EMAIL || (me as any).role === 'admin');

  useEffect(() => {
    if (!meLoading && !isAdmin) setLocation('/');
  }, [meLoading, isAdmin, setLocation]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/users`, { headers }),
        fetch(`${API_BASE}/api/admin/stats`, { headers }),
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch {
      toast({ title: 'Failed to load admin data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin, token]);

  const openEdit = (user: AdminUser) => {
    setSelectedUser(user);
    setEditName(user.name ?? '');
    setEditRole(user.role);
    setEditFlags({ ...user.featureFlags });
    setEditPlanName(user.planName ?? '');
    setEditPlanPrice(user.planPriceCents != null ? String(user.planPriceCents / 100) : '');
    setConfirmDelete(false);
  };

  const closeEdit = () => {
    setSelectedUser(null);
    setConfirmDelete(false);
  };

  const toggleFlag = (key: string, adminDefault: boolean) => {
    setEditFlags(prev => {
      const current = getEffectiveState(prev, key, adminDefault);
      const next = !current;
      const isDefault = next === adminDefault;
      if (isDefault) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: next };
    });
  };

  const saveUser = async () => {
    if (!selectedUser || !token) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editName || null,
          role: editRole,
          featureFlags: editFlags,
          planName: editPlanName || null,
          planPriceCents: editPlanPrice ? Math.round(parseFloat(editPlanPrice) * 100) : null,
        }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
      toast({ title: 'User updated' });
      closeEdit();
    } catch {
      toast({ title: 'Failed to save changes', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async () => {
    if (!selectedUser || !token) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
      setStats(prev => ({ ...prev, totalUsers: prev.totalUsers - 1 }));
      toast({ title: 'User deleted' });
      closeEdit();
    } catch {
      toast({ title: 'Failed to delete user', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.name ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const overrideCount = (flags: Record<string, boolean>) => Object.keys(flags).length;

  const flagGroups = FEATURE_FLAGS.reduce<Record<string, FeatureFlagDef[]>>((acc, f) => {
    if (!acc[f.group]) acc[f.group] = [];
    acc[f.group]!.push(f);
    return acc;
  }, {});

  if (meLoading) return null;

  return (
    <Layout>
      <div className="space-y-8 relative">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">User Management</h1>
          <p className="text-muted-foreground mt-1">Manage platform users, roles, and feature access</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-2xl bg-background border border-border/50 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Total Users</span>
            </div>
            <p className="text-3xl font-display font-bold text-primary">{stats.totalUsers}</p>
          </div>
          <div className="rounded-2xl bg-background border border-border/50 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Store className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Connected Stores</span>
            </div>
            <p className="text-3xl font-display font-bold text-primary">{stats.totalStores}</p>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search users…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-background border border-border/60 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>
          <span className="text-sm text-muted-foreground">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* User table */}
        <div className="rounded-2xl border border-border/50 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/20">
                <th className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">User</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Role</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Stores</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Feature Overrides</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Joined</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i} className="border-b border-border/50">
                      {Array.from({ length: 6 }).map((_, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-muted/50 rounded animate-pulse" style={{ width: j === 0 ? 160 : j === 5 ? 32 : 60 }} />
                        </td>
                      ))}
                    </tr>
                  ))
                : filtered.map(user => (
                    <tr
                      key={user.id}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-primary">
                              {(user.name ?? user.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{user.name || <span className="text-muted-foreground italic">No name</span>}</p>
                            <p className="text-xs text-muted-foreground">{user.email.toLowerCase()}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        {user.role === 'admin' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/15 text-primary text-xs font-semibold">
                            <ShieldCheck className="w-3 h-3" /> Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/50 text-muted-foreground text-xs font-medium">
                            <Shield className="w-3 h-3" /> User
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-foreground font-mono">{user.storeCount}</td>
                      <td className="px-5 py-4">
                        {overrideCount(user.featureFlags) > 0
                          ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 text-xs font-medium">
                              {overrideCount(user.featureFlags)} override{overrideCount(user.featureFlags) !== 1 ? 's' : ''}
                            </span>
                          : <span className="text-slate-600 text-xs">Default</span>
                        }
                      </td>
                      <td className="px-5 py-4 text-muted-foreground text-xs">
                        {fmtDate(user.createdAt)}
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => openEdit(user)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
              }
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-muted-foreground">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit panel */}
      <AnimatePresence>
        {selectedUser && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-30"
              onClick={closeEdit}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="fixed right-0 top-0 h-full w-[480px] bg-card border-l border-border/60 z-40 flex flex-col"
            >
              {/* Panel header */}
              <div className="flex items-center justify-between p-6 border-b border-border/60">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">
                      {(selectedUser.name ?? selectedUser.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{selectedUser.name || 'Unnamed User'}</p>
                    <p className="text-xs text-muted-foreground">{selectedUser.email}</p>
                  </div>
                </div>
                <button onClick={closeEdit} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-8">
                {/* Name */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Display Name</label>
                  <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Enter name…"
                    className="w-full px-4 py-2.5 rounded-xl bg-background border border-border/60 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                  />
                </div>

                {/* Role */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['user', 'admin'] as const).map(r => (
                      <button
                        key={r}
                        onClick={() => setEditRole(r)}
                        className={cn(
                          "flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all",
                          editRole === r
                            ? "bg-primary/10 border-primary/40 text-primary"
                            : "bg-muted/20 border-border/60 text-muted-foreground hover:border-border"
                        )}
                      >
                        {r === 'admin' ? <ShieldCheck className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                        {r === 'admin' ? 'Admin' : 'Standard User'}
                      </button>
                    ))}
                  </div>
                  {editRole === 'admin' && (
                    <p className="text-xs text-amber-400/80 flex items-center gap-1.5 mt-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                      Admins have full access to all features and this panel
                    </p>
                  )}
                </div>

                {/* Plan / Billing */}
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Billing Plan</label>
                    <p className="text-xs text-slate-600 mt-1">Set a custom plan for this user. Price is in AUD per month.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Plan Name</label>
                      <input
                        value={editPlanName}
                        onChange={e => setEditPlanName(e.target.value)}
                        placeholder="e.g. Starter, Growth…"
                        className="w-full px-3 py-2 rounded-xl bg-background border border-border/60 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Monthly Price (AUD $)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editPlanPrice}
                        onChange={e => setEditPlanPrice(e.target.value)}
                        placeholder="e.g. 99.00"
                        className="w-full px-3 py-2 rounded-xl bg-background border border-border/60 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                  </div>
                  {selectedUser?.stripeCustomerId && (
                    <p className="text-[10px] text-slate-600">
                      Stripe customer: <span className="font-mono text-muted-foreground">{selectedUser.stripeCustomerId}</span>
                    </p>
                  )}
                </div>

                {/* Feature flags */}
                <div className="space-y-5">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Feature Access</label>
                    <p className="text-xs text-slate-600 mt-1">Toggle features on or off for this user. Defaults match the platform standard.</p>
                  </div>

                  {Object.entries(flagGroups).map(([group, flags]) => (
                    <div key={group} className="space-y-1">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600 px-1 mb-2">{group}</p>
                      {flags.map(flag => {
                        const effective = getEffectiveState(editFlags, flag.key, flag.adminDefault);
                        const isOverridden = flag.key in editFlags;
                        return (
                          <div
                            key={flag.key}
                            className={cn(
                              "flex items-center justify-between px-4 py-3 rounded-xl border transition-all",
                              isOverridden
                                ? "bg-primary/5 border-primary/20"
                                : "bg-muted/20 border-border/50"
                            )}
                          >
                            <div className="flex-1 min-w-0 pr-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground">{flag.label}</span>
                                {flag.adminDefault && !isOverridden && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground font-medium">Admin only</span>
                                )}
                                {isOverridden && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-medium">Custom</span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">{flag.description}</p>
                            </div>
                            <button
                              onClick={() => toggleFlag(flag.key, flag.adminDefault)}
                              className="flex-shrink-0"
                            >
                              {effective
                                ? <ToggleRight className="w-8 h-8 text-primary" />
                                : <ToggleLeft className="w-8 h-8 text-slate-600" />
                              }
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Danger zone */}
                {selectedUser.email !== ADMIN_EMAIL && (
                  <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-red-400">Danger Zone</p>
                    {!confirmDelete ? (
                      <button
                        onClick={() => setConfirmDelete(true)}
                        className="flex items-center gap-2 text-sm text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete this user account
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-red-700 dark:text-red-300">This will permanently delete the user and all their data. Are you sure?</p>
                        <div className="flex gap-2">
                          <button
                            onClick={deleteUser}
                            disabled={saving}
                            className="flex-1 px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-700 dark:text-red-300 text-sm font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50"
                          >
                            {saving ? 'Deleting…' : 'Yes, delete'}
                          </button>
                          <button
                            onClick={() => setConfirmDelete(false)}
                            className="flex-1 px-3 py-2 rounded-lg bg-muted/50 border border-border/60 text-muted-foreground text-sm font-medium hover:bg-muted/50 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-border/60 flex gap-3">
                <button
                  onClick={closeEdit}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-border/60 text-muted-foreground text-sm font-medium hover:bg-muted/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveUser}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-black text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving…' : <><Check className="w-4 h-4" /> Save Changes</>}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Layout>
  );
}
